// Card de una corrección pendiente o auto-aplicada (Fase 39-40).
//
// Muestra el timestamp, texto original, sugerencia de la IA, y acciones:
//   - ✅ Aceptar sugerencia
//   - ✏️ Corregir (expande input inline con autocomplete del glosario)
//   - ⏭ OK como está
//
// Para correcciones auto-aplicadas (vista colapsada), permite revertir.
//
// Fase 40: botón ▶ Escuchar 5s con video inline para MP4.

import { useState, useMemo } from "react";
import styled from "styled-components";
import type { AppliedCorrection, PendingCorrection, GlossaryTerm } from "../../platform/api";
import type { CorrectionDecision } from "../../state/atoms";
import { SampleVideoPlayer } from "./SampleVideoPlayer.tsx";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTimestamp(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

const TYPE_LABELS: Record<string, string> = {
  speaker_identification: "Speaker",
  technical_term: "Término técnico",
  proper_name: "Nombre propio",
  language_mix: "Idioma",
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const CardWrapper = styled.div<{ $resolved: boolean }>`
  padding: 16px;
  border: 1px solid ${({ theme, $resolved }) =>
    $resolved ? theme.color.success : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme, $resolved }) =>
    $resolved ? theme.color.successMuted : theme.color.surface};
  margin-bottom: 12px;
  opacity: ${({ $resolved }) => ($resolved ? 0.75 : 1)};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Timestamp = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.accent};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const TypeBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  padding: 2px 8px;
  border-radius: 999px;
`;

const ConfidenceBadge = styled.span<{ $confidence: string }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme, $confidence }) =>
    $confidence === "high" ? theme.color.success :
    $confidence === "medium" ? theme.color.warning :
    theme.color.error};
  margin-left: auto;
`;

const TextBlock = styled.div`
  margin-bottom: 8px;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textDim};
  margin-bottom: 2px;
`;

const Text = styled.div<{ $strikethrough?: boolean }>`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  padding: 8px;
  background: ${({ theme }) => theme.color.background};
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.border};
  text-decoration: ${({ $strikethrough }) => ($strikethrough ? "line-through" : "none")};
`;

const SuggestionText = styled(Text)`
  border-color: ${({ theme }) => theme.color.accent};
  background: ${({ theme }) => theme.color.accentMuted};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant: "accept" | "edit" | "skip" | "revert" }>`
  padding: 6px 14px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid;
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
  background: ${({ theme, $variant }) =>
    $variant === "accept" ? theme.color.successMuted :
    $variant === "revert" ? theme.color.warningMuted :
    theme.color.surface};
  color: ${({ theme, $variant }) =>
    $variant === "accept" ? theme.color.success :
    $variant === "revert" ? theme.color.warning :
    $variant === "edit" ? theme.color.accent :
    theme.color.textMuted};
  border-color: ${({ theme, $variant }) =>
    $variant === "accept" ? theme.color.success :
    $variant === "revert" ? theme.color.warning :
    $variant === "edit" ? theme.color.accent :
    theme.color.border};
  &:hover {
    opacity: 0.85;
  }
`;

const EditRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  align-items: flex-start;
`;

const EditInputWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const EditInput = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  font-family: ${({ theme }) => theme.font.family};
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.accentHover};
  }
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 160px;
  overflow-y: auto;
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  box-shadow: ${({ theme }) => theme.shadow.md};
  z-index: 10;
`;

const AutocompleteItem = styled.button`
  display: block;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: none;
  text-align: left;
  font-size: ${({ theme }) => theme.font.size.sm};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.accentMuted};
  }
`;

const DecisionLabel = styled.div<{ $kind: string }>`
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme, $kind }) =>
    $kind === "accept" ? theme.color.success :
    $kind === "edit" ? theme.color.accent :
    theme.color.textMuted};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  margin-top: 8px;
`;

const PlayButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.color.accent : theme.color.border};
  background: ${({ theme, $active }) =>
    $active ? theme.color.accentMuted : theme.color.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.color.accent : theme.color.textMuted};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  flex-shrink: 0;
  &:hover {
    border-color: ${({ theme }) => theme.color.accent};
    color: ${({ theme }) => theme.color.accent};
  }
`;

const VideoContainer = styled.div`
  margin-top: 8px;
`;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  /** La corrección (pendiente o auto-aplicada). */
  item: PendingCorrection | AppliedCorrection;
  /** Decisión actual del usuario para este item, si existe. */
  decision?: CorrectionDecision;
  /** La lista de términos del glosario para autocomplete. */
  glossaryTerms: GlossaryTerm[];
  /** Callback al cambiar la decisión. */
  onChange: (id: string, decision: CorrectionDecision) => void;
  /** ID del sample que se está reproduciendo, o null. */
  playingId?: string | null;
  /** URL del video (presente solo si el meeting es MP4). */
  videoUrl?: string | null;
  /** Callback al presionar ▶. */
  onPlaySample?: (id: string, startTime: number) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CorrectionItemCard({
  item,
  decision,
  glossaryTerms,
  onChange,
  playingId,
  videoUrl,
  onPlaySample,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [showAutocomplete, setShowAutocomplete] = useState(false);

  const isApplied = "after" in item;
  const suggestion = isApplied ? item.after : item.suggestion;
  const original = item.before;
  const typeLabel = TYPE_LABELS[item.type] ?? item.type;
  const isResolved = decision != null;

  const autocompleteMatches = useMemo(() => {
    if (!editValue || editValue.length < 1) return [];
    const lower = editValue.toLowerCase();
    return glossaryTerms
      .filter(
        (t) =>
          t.term.toLowerCase().includes(lower) ||
          t.variants?.some((v) => v.toLowerCase().includes(lower)),
      )
      .slice(0, 8);
  }, [editValue, glossaryTerms]);

  const handleAccept = () => {
    onChange(item.id, { kind: "accept" });
  };

  const handleSkip = () => {
    onChange(item.id, { kind: "skip" });
  };

  const handleStartEdit = () => {
    setEditValue(suggestion);
    setEditing(true);
  };

  const handleConfirmEdit = () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== suggestion) {
      onChange(item.id, { kind: "edit", correctedText: trimmed });
    } else if (trimmed === suggestion) {
      onChange(item.id, { kind: "accept" });
    }
    setEditing(false);
    setShowAutocomplete(false);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setShowAutocomplete(false);
  };

  const handleRevert = () => {
    // Revertir una corrección auto-aplicada es como un "skip" (dejar el original).
    onChange(item.id, { kind: "skip" });
  };

  const handleAutocompleteSelect = (term: string) => {
    // Reemplazar la última "palabra" del input con el término canónico.
    const words = editValue.split(/\s+/);
    words[words.length - 1] = term;
    setEditValue(words.join(" "));
    setShowAutocomplete(false);
  };

  return (
    <CardWrapper $resolved={isResolved}>
      <CardHeader>
        {onPlaySample && (
          <PlayButton
            $active={playingId === item.id}
            onClick={(e) => {
              e.stopPropagation();
              onPlaySample(item.id, item.timestamp);
            }}
            title="Escuchar 5s"
          >
            {playingId === item.id ? "⏸" : "▶"}
          </PlayButton>
        )}
        <Timestamp>{formatTimestamp(item.timestamp)}</Timestamp>
        <TypeBadge>{typeLabel}</TypeBadge>
        <ConfidenceBadge $confidence={item.confidence}>
          {item.confidence === "high" ? "Alta" :
           item.confidence === "medium" ? "Media" : "Baja"}
        </ConfidenceBadge>
      </CardHeader>

      {videoUrl && playingId === item.id && (
        <VideoContainer>
          <SampleVideoPlayer src={videoUrl} startTime={item.timestamp} />
        </VideoContainer>
      )}

      <TextBlock>
        <Label>Original</Label>
        <Text $strikethrough={decision?.kind === "accept" || decision?.kind === "edit"}>
          {original}
        </Text>
      </TextBlock>

      <TextBlock>
        <Label>Sugerencia IA</Label>
        <SuggestionText>{suggestion}</SuggestionText>
      </TextBlock>

      {item.reason && (
        <TextBlock>
          <Label>Razón</Label>
          <Text style={{ fontSize: "0.8rem", fontStyle: "italic" }}>{item.reason}</Text>
        </TextBlock>
      )}

      {decision && !editing && (
        <DecisionLabel $kind={decision.kind}>
          {decision.kind === "accept" && "✅ Sugerencia aceptada"}
          {decision.kind === "edit" && `✏️ Corregido manualmente: "${decision.correctedText}"`}
          {decision.kind === "skip" && "⏭ Sin cambios — el original es correcto"}
        </DecisionLabel>
      )}

      {/* Para pendientes: acciones completas. Para auto-aplicadas: solo revertir. */}
      {!isApplied && !decision && (
        <>
          {!editing ? (
            <Actions>
              <ActionButton $variant="accept" onClick={handleAccept}>
                ✅ Aceptar sugerencia
              </ActionButton>
              <ActionButton $variant="edit" onClick={handleStartEdit}>
                ✏️ Corregir
              </ActionButton>
              <ActionButton $variant="skip" onClick={handleSkip}>
                ⏭ OK como está
              </ActionButton>
            </Actions>
          ) : (
            <>
              <EditRow>
                <EditInputWrapper>
                  <EditInput
                    value={editValue}
                    onChange={(e) => {
                      setEditValue(e.target.value);
                      setShowAutocomplete(e.target.value.length > 0);
                    }}
                    onFocus={() => setShowAutocomplete(editValue.length > 0)}
                    onBlur={() => setTimeout(() => setShowAutocomplete(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleConfirmEdit();
                      if (e.key === "Escape") handleCancelEdit();
                    }}
                    placeholder="Escribí la corrección..."
                    autoFocus
                  />
                  {showAutocomplete && autocompleteMatches.length > 0 && (
                    <AutocompleteDropdown>
                      {autocompleteMatches.map((t) => (
                        <AutocompleteItem
                          key={t.id}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleAutocompleteSelect(t.term);
                          }}
                        >
                          {t.term}
                          {t.variants && t.variants.length > 0 &&
                            ` — ${t.variants.slice(0, 3).join(", ")}`}
                        </AutocompleteItem>
                      ))}
                    </AutocompleteDropdown>
                  )}
                </EditInputWrapper>
                <ActionButton $variant="accept" onClick={handleConfirmEdit}>
                  ✓ Confirmar
                </ActionButton>
                <ActionButton $variant="skip" onClick={handleCancelEdit}>
                  Cancelar
                </ActionButton>
              </EditRow>
            </>
          )}
        </>
      )}

      {isApplied && !decision && (
        <Actions>
          <ActionButton $variant="revert" onClick={handleRevert}>
            ↩ Revertir — dejar original
          </ActionButton>
        </Actions>
      )}
    </CardWrapper>
  );
}
